
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * AssetInfo struct
 */
class AssetInfo {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!string=} currency
   * @param {!number=} actualVersion
   * @param {!number=} amount
   * @param {!number=} credit
   * @param {!number=} locked
   * @constructor
   */
  constructor (login = new UInt64(0, 0), currency = '', actualVersion = new UInt64(0, 0), amount = 0.0, credit = 0.0, locked = 0.0) {
    this.Login = login
    this.Currency = currency
    this.ActualVersion = actualVersion
    this.Amount = amount
    this.Credit = credit
    this.Locked = locked
  }

  /**
   * Copy struct (shallow copy)
   * @this {!AssetInfo}
   * @param {!AssetInfo} other Other struct
   * @returns {!AssetInfo} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.Currency != null) {
      this.Currency = other.Currency
    } else {
      this.Currency = null
    }
    if (other.ActualVersion != null) {
      this.ActualVersion = UInt64.fromNumber(other.ActualVersion)
    } else {
      this.ActualVersion = null
    }
    if (other.Amount != null) {
      this.Amount = other.Amount
    } else {
      this.Amount = null
    }
    if (other.Credit != null) {
      this.Credit = other.Credit
    } else {
      this.Credit = null
    }
    if (other.Locked != null) {
      this.Locked = other.Locked
    } else {
      this.Locked = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!AssetInfo}
   * @returns {!AssetInfo} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new AssetInfoModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new AssetInfoModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!AssetInfo}
   * @param {!AssetInfo} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof AssetInfo)) {
      throw new TypeError('Instance of AssetInfo is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!AssetInfo}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      Currency: ((this.Currency != null) ? this.Currency : null),
      ActualVersion: ((this.ActualVersion != null) ? this.ActualVersion.toNumber() : null),
      Amount: ((this.Amount != null) ? this.Amount : null),
      Credit: ((this.Credit != null) ? this.Credit : null),
      Locked: ((this.Locked != null) ? this.Locked : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return AssetInfo.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!AssetInfo} other Object value
   * @returns {!AssetInfo} Created struct
   */
  static fromObject (other) {
    return new AssetInfo().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!AssetInfo}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return AssetInfo.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!AssetInfo}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5077
  }
}

export { AssetInfo };

/**
 * Fast Binary Encoding AssetInfo field model
 */
class FieldModelAssetInfo extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Currency = new fbe.FieldModelString(buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.ActualVersion = new fbe.FieldModelUInt64(buffer, this.Currency.fbeOffset + this.Currency.fbeSize)
    this.Amount = new fbe.FieldModelDouble(buffer, this.ActualVersion.fbeOffset + this.ActualVersion.fbeSize)
    this.Credit = new fbe.FieldModelDouble(buffer, this.Amount.fbeOffset + this.Amount.fbeSize)
    this.Locked = new fbe.FieldModelDouble(buffer, this.Credit.fbeOffset + this.Credit.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.Currency.fbeSize + this.ActualVersion.fbeSize + this.Amount.fbeSize + this.Credit.fbeSize + this.Locked.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.Currency.fbeExtra + this.ActualVersion.fbeExtra + this.Amount.fbeExtra + this.Credit.fbeExtra + this.Locked.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelAssetInfo.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5077
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelAssetInfo}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelAssetInfo}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Currency.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Currency.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Currency.fbeSize

    if ((fbeCurrentSize + this.ActualVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ActualVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActualVersion.fbeSize

    if ((fbeCurrentSize + this.Amount.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Amount.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Amount.fbeSize

    if ((fbeCurrentSize + this.Credit.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Credit.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Credit.fbeSize

    if ((fbeCurrentSize + this.Locked.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Locked.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Locked.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelAssetInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelAssetInfo}
   * @param {!AssetInfo} fbeValue Default value, defaults is new AssetInfo()
   * @returns {!AssetInfo} AssetInfo value
   */
  get (fbeValue = new AssetInfo()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelAssetInfo}
   * @param {!AssetInfo} fbeValue AssetInfo value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Currency.fbeSize) <= fbeStructSize) {
      fbeValue.Currency = this.Currency.get()
    } else {
      fbeValue.Currency = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Currency.fbeSize

    if ((fbeCurrentSize + this.ActualVersion.fbeSize) <= fbeStructSize) {
      fbeValue.ActualVersion = this.ActualVersion.get()
    } else {
      fbeValue.ActualVersion = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ActualVersion.fbeSize

    if ((fbeCurrentSize + this.Amount.fbeSize) <= fbeStructSize) {
      fbeValue.Amount = this.Amount.get()
    } else {
      fbeValue.Amount = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Amount.fbeSize

    if ((fbeCurrentSize + this.Credit.fbeSize) <= fbeStructSize) {
      fbeValue.Credit = this.Credit.get()
    } else {
      fbeValue.Credit = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Credit.fbeSize

    if ((fbeCurrentSize + this.Locked.fbeSize) <= fbeStructSize) {
      fbeValue.Locked = this.Locked.get()
    } else {
      fbeValue.Locked = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Locked.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelAssetInfo}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelAssetInfo}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelAssetInfo}
   * @param {!AssetInfo} fbeValue AssetInfo value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelAssetInfo}
   * @param {AssetInfo} fbeValue AssetInfo value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.Currency.set(fbeValue.Currency)
    this.ActualVersion.set(fbeValue.ActualVersion)
    this.Amount.set(fbeValue.Amount)
    this.Credit.set(fbeValue.Credit)
    this.Locked.set(fbeValue.Locked)
  }
}

export { FieldModelAssetInfo };

/**
 * Fast Binary Encoding AssetInfo model
 */
class AssetInfoModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelAssetInfo(this.buffer, 4)
  }

  /**
   * Get the AssetInfo model
   * @this {!AssetInfoModel}
   * @returns {!FieldModelAssetInfo} model AssetInfo model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!AssetInfoModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!AssetInfoModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return AssetInfoModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!AssetInfoModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelAssetInfo.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!AssetInfoModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!AssetInfoModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!AssetInfoModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!AssetInfoModel}
   * @param {!AssetInfo} value AssetInfo value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!AssetInfoModel}
   * @param {!AssetInfo} value AssetInfo value, defaults is new AssetInfo()
   * @return {!object} Deserialized AssetInfo value and its size
   */
  deserialize (value = new AssetInfo()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new AssetInfo(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new AssetInfo(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!AssetInfoModel}
   * @param {!number} prev Previous AssetInfo model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { AssetInfoModel };
